import {
  faCircleInfo,
  faFilePdf,
  faPen,
  faPlus,
  faStop,
  faTrashCan,
  faWandMagicSparkles,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormEvent, KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { AutoGrowTextarea } from "../../chat/AutoGrowTextarea";
import { markdownComponents } from "../../chat/Markdown";
import { useWorkspaceKavel } from "../kavel";
import { useWorkspaceSearch } from "../search";
import { ActionRunCard, hasCard } from "./ActionRunCard";
import { activeParcelFor, chipLabel, chipParcel } from "./activeParcel";
import { filterButtonTitle, filterCountLabel } from "./filterCount";
import { SkillBadges } from "./SkillBadges";
import { initialValues, matchingSkills, moveHighlight, slashQuery } from "./skillForm";
import { SkillForm } from "./SkillPanel";
import { useWorkspace } from "./store";
import {
  FilterSuggestion,
  ReportProposal,
  ResearchProposal,
  SkillDefinition,
  SkillOption,
  SkillRun,
  WorkspaceMessage,
  pdfPending,
} from "./types";

// De rapportknop in de sessiebalk start deze skill: een beurt van het model
// die het rapport schrijft, zonder formulier.
const REPORT_SKILL = { name: "conversation_report", label: "Gespreksrapport" };

const dateLabel = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "short" }) : "";

const FilterChip = ({ filter, onApply }: { filter: FilterSuggestion; onApply?: () => void }) => {
  const activeId = useWorkspaceSearch((s) => s.filterId);
  const apply = useWorkspaceSearch((s) => s.apply);
  const active = activeId === filter.id;
  const count = filterCountLabel(filter.total, filter.capped);
  // Zero hits is a measured outcome: the button should then do nothing.
  const empty = filter.total === 0;

  return (
    <button
      onClick={() => {
        apply(filter.id, filter.criteria, filter.label, filter.total ?? undefined);
        onApply?.();
      }}
      aria-pressed={active}
      disabled={empty}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] ${
        empty
          ? "cursor-default border-gray-200 bg-gray-50 text-gray-400"
          : active
            ? "border-dark-green bg-yellow/30"
            : "border-yellow bg-bright-yellow hover:bg-yellow"
      }`}
      title={filterButtonTitle(filter.total, filter.capped)}
    >
      <span className={`italic ${empty ? "" : "text-black"}`}>{filter.label || "Filter"}</span>
      {count && <span className={empty ? "" : "text-dark-green"}>{count}</span>}
    </button>
  );
};

const timeLabel = (iso?: string) =>
  iso ? new Date(iso).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" }) : "";

// Drie stipjes terwijl het eerste stukje antwoord onderweg is.
const Typing = () => (
  <span className="flex gap-1 py-1">
    {[0, 150, 300].map((delay) => (
      <span
        key={delay}
        className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400"
        style={{ animationDelay: `${delay}ms` }}
      />
    ))}
  </span>
);

const ReportProposalCard = ({ proposal }: { proposal: ReportProposal }) => {
  const createReportPdf = useWorkspace((s) => s.createReportPdf);
  // Alleen het verzoek zelf kan hier mislukken; het maken meldt zich via pdfStatus.
  const [requestFailed, setRequestFailed] = useState(false);
  const pending = pdfPending(proposal.pdfStatus);
  const make = async () => {
    setRequestFailed(false);
    const ok = await createReportPdf(proposal.id);
    setRequestFailed(!ok);
  };
  const failure = requestFailed
    ? "Aanvragen mislukt; probeer het opnieuw."
    : proposal.pdfStatus === "failed"
      ? proposal.pdfError || "Maken mislukt; probeer het opnieuw."
      : null;
  return (
    <div className="mt-1.5 max-w-[85%] rounded-lg border border-yellow bg-yellow/10 p-2 text-xs">
      <p className="font-bold">
        <FontAwesomeIcon icon={faFilePdf} size="sm" className="mr-1" />
        Rapport: {proposal.title}
      </p>
      {proposal.situation && (
        <div className="mt-1 text-gray-700">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {proposal.situation}
          </ReactMarkdown>
        </div>
      )}
      {proposal.conclusion && (
        <div className="mt-1 border-t border-yellow/60 pt-1 text-gray-700">
          <p className="font-bold">Conclusie</p>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {proposal.conclusion}
          </ReactMarkdown>
        </div>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {proposal.fileUrl ? (
          <a
            href={proposal.fileUrl}
            target="_blank"
            rel="noreferrer"
            className="rounded bg-bright-yellow px-2.5 py-1 font-bold hover:bg-yellow"
          >
            Open PDF
          </a>
        ) : null}
        <button
          type="button"
          onClick={make}
          disabled={pending}
          className="rounded border border-yellow px-2.5 py-1 font-bold hover:bg-yellow/40 disabled:opacity-50"
        >
          {pending ? "PDF wordt gemaakt…" : proposal.fileUrl ? "Opnieuw maken" : "Maak PDF"}
        </button>
        {failure && <span className="text-red-600">{failure}</span>}
      </div>
    </div>
  );
};

// Het onderzoek dat een gestarte run opleverde, uit zijn actie.
const startedResearch = (run?: SkillRun) =>
  run?.actions?.find((action) => action.researchId)?.researchId ?? undefined;

// Een skill in het gesprek. Aangeboden: een formulier met Start. Gestart: wat
// zijn acties deden of nog vragen (een onderzoek, een document, een bedrag om te
// bevestigen). Een filter heeft zijn knop en een rapport zijn eigen kaart.
const SkillRunCard = ({
  run,
  message,
  parcels,
  onResearchStarted,
}: {
  run: SkillRun;
  message: WorkspaceMessage;
  parcels: SkillOption[];
  onResearchStarted?: (researchId: number) => void;
}) => {
  const startSkillRun = useWorkspace((s) => s.startSkillRun);
  const startSkillTurn = useWorkspace((s) => s.startSkillTurn);
  const isStreaming = useWorkspace((s) => s.isStreaming);
  // Runs van vóór elke uitvoering een actie was: een filter en een rapport
  // tonen hun eigen record.
  if (run.filterId || run.reportId) return null;

  if (run.status === "started") {
    const cards = (run.actions ?? []).filter(hasCard);
    const legacyResearchId = message.researchProposals?.find(
      (proposal) => proposal.id === run.researchProposalId
    )?.researchId;
    if (!cards.length && !legacyResearchId) return null;
    return (
      <div className="mt-1.5 w-full max-w-[85%] text-xs">
        {cards.map((action) => (
          <ActionRunCard key={action.id} run={action} onOpenResearch={onResearchStarted} />
        ))}
        {!cards.length && legacyResearchId && (
          <button
            type="button"
            onClick={() => onResearchStarted?.(legacyResearchId)}
            className="rounded border border-yellow px-2.5 py-1 font-bold hover:bg-yellow/40"
          >
            Bekijk onderzoek
          </button>
        )}
      </div>
    );
  }

  const submit = async (values: Record<string, any>) => {
    if (run.turn) return startSkillTurn({ skill_run_id: run.id }, values, run.label);
    const outcome = await startSkillRun(run.id, values);
    if (!outcome.ok) return outcome.errors;
    const started = startedResearch(outcome.message.skillRuns.find((item) => item.id === run.id));
    if (started) onResearchStarted?.(started);
    return undefined;
  };

  return (
    <div className="mt-1.5 w-full max-w-[85%] rounded-lg border border-yellow bg-yellow/10 p-2 text-xs">
      <p className="mb-1 font-bold">
        <FontAwesomeIcon icon={faWandMagicSparkles} size="sm" className="mr-1" />
        {run.label}
        <SkillBadges skill={run} />
      </p>
      {run.status === "failed" && run.error && <p className="mb-1 text-red-600">{run.error}</p>}
      <SkillForm
        fields={run.fields}
        initial={initialValues(run.fields, run.values)}
        parcels={parcels}
        disabled={isStreaming}
        onSubmit={submit}
      />
    </div>
  );
};

const Message = ({
  message,
  onApplyFilter,
  showFilterTip,
  onResearchProposal,
  onResearchStarted,
  parcels,
}: {
  message: WorkspaceMessage;
  onApplyFilter?: () => void;
  showFilterTip?: boolean;
  onResearchProposal?: (proposal: ResearchProposal) => void;
  onResearchStarted?: (researchId: number) => void;
  parcels: SkillOption[];
}) => {
  const mine = message.role === "user";
  const runs = message.skillRuns ?? [];
  // Voorstellen van vóór de skills hebben geen run; die houden hun oude kaart.
  const legacyProposals = (message.researchProposals ?? []).filter(
    (proposal) => !runs.some((run) => run.researchProposalId === proposal.id)
  );
  // Een antwoord dat alleen uit een voorstel bestond, heeft geen tekst; dan geen
  // lege bel met typ-indicator, alleen de voorstelkaart eronder.
  const proposalOnly =
    !mine &&
    !message.content &&
    ((message.reportProposals?.length ?? 0) > 0 ||
      (message.researchProposals?.length ?? 0) > 0 ||
      message.filters.length > 0 ||
      runs.length > 0);
  return (
    <div className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
      {!proposalOnly && (
        <div
          className={`max-w-[85%] rounded-2xl px-3 py-2 text-xs wrap-break-word ${
            mine
              ? "rounded-br-sm bg-light-green text-black"
              : "rounded-bl-sm border border-gray-200 bg-white text-black"
          }`}
        >
          {mine ? (
            <p className="whitespace-pre-wrap">{message.content}</p>
          ) : message.content ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {message.content}
            </ReactMarkdown>
          ) : (
            <Typing />
          )}
          {!message.finished && <p className="pt-1 text-[11px] italic text-gray-500">afgebroken</p>}
        </div>
      )}

      {message.filters.length > 0 && (
        <div className="max-w-[85%] pt-1.5">
          {/* The icon sits beside the chips, not among them: a long filter label
              fills the whole row, and wrapping would drop it onto its own line. */}
          <div className="flex items-start gap-1.5">
            <div className="flex min-w-0 flex-wrap gap-1.5">
              {message.filters.map((filter) => (
                <FilterChip key={filter.id} filter={filter} onApply={onApplyFilter} />
              ))}
            </div>
            {!showFilterTip && (
              <span
                tabIndex={0}
                role="img"
                aria-label="Uitleg over de filterknop"
                title="Klik op de gele filterknop om de gevonden percelen op de kaart te tonen."
                className="inline-flex shrink-0 items-center pt-1.5 text-gray-400 hover:text-gray-600 focus:text-gray-600 focus:outline-none"
              >
                <FontAwesomeIcon icon={faCircleInfo} size="sm" />
              </span>
            )}
          </div>
          {showFilterTip && (
            <p className="pt-1 text-[11px] leading-snug text-gray-500">
              Tip: klik op de gele filterknop om de gevonden percelen op de kaart te tonen.
            </p>
          )}
        </div>
      )}
      {runs.map((run) => (
        <SkillRunCard
          key={run.id}
          run={run}
          message={message}
          parcels={parcels}
          onResearchStarted={onResearchStarted}
        />
      ))}
      {legacyProposals.map((proposal) => (
        <div
          key={proposal.id}
          className="mt-1.5 max-w-[85%] rounded-lg border border-yellow bg-yellow/10 p-2 text-xs"
        >
          <p className="font-bold">Onderzoek {proposal.parcelName}</p>
          {proposal.extraQuestion && (
            <>
              <p className="mt-0.5 text-gray-600">{proposal.extraQuestion}</p>
              <p className="mt-0.5 text-[11px] text-gray-500">
                Alleen deze vraag; de standaardonderwerpen blijven buiten beschouwing.
              </p>
            </>
          )}
          <button
            type="button"
            onClick={() => onResearchProposal?.(proposal)}
            className="mt-2 rounded bg-bright-yellow px-2.5 py-1 font-bold hover:bg-yellow"
          >
            Onderzoek starten
          </button>
        </div>
      ))}
      {message.reportProposals?.map((proposal) => (
        <ReportProposalCard key={proposal.id} proposal={proposal} />
      ))}
      {message.created && (
        <span className="px-1 pt-0.5 text-[10px] text-gray-400">{timeLabel(message.created)}</span>
      )}
    </div>
  );
};

const SessionHeader = ({
  onSelect,
  onReport,
}: {
  onSelect: (sessionId?: number) => void;
  onReport: () => void;
}) => {
  const { sessions, activeSessionId, renameSession, deleteSession, isStreaming } = useWorkspace();
  const active = useMemo(
    () => sessions.find((s) => s.id === activeSessionId),
    [sessions, activeSessionId]
  );

  return (
    <div className="flex items-center gap-1 border-b px-2 py-1.5">
      <select
        value={activeSessionId ?? ""}
        onChange={(e) => onSelect(e.target.value ? Number(e.target.value) : undefined)}
        className="flex-1 min-w-0 truncate border border-gray-200 rounded px-2 py-1 text-xs"
      >
        {!activeSessionId && <option value="">Nieuwe chat</option>}
        {sessions.map((session) => (
          <option key={session.id} value={session.id}>
            {session.title || "Zonder titel"} - {dateLabel(session.modified)}
          </option>
        ))}
      </select>
      <button
        onClick={() => onSelect(undefined)}
        title="Nieuwe chat"
        className="px-2 py-1 text-gray-500 hover:text-black"
      >
        <FontAwesomeIcon icon={faPlus} size="sm" />
      </button>
      {active && (
        <>
          <button
            onClick={onReport}
            disabled={isStreaming}
            title="Rapport van dit gesprek (PDF)"
            className="px-2 py-1 text-gray-500 hover:text-black disabled:opacity-40"
          >
            <FontAwesomeIcon icon={faFilePdf} size="sm" />
          </button>
          <button
            onClick={() => {
              const title = prompt("Nieuwe titel", active.title);
              if (title?.trim()) renameSession(active.id, title.trim());
            }}
            title="Hernoemen"
            className="px-2 py-1 text-gray-500 hover:text-black"
          >
            <FontAwesomeIcon icon={faPen} size="sm" />
          </button>
          <button
            onClick={() => {
              if (confirm("Dit gesprek verwijderen?")) {
                deleteSession(active.id);
                onSelect(undefined);
              }
            }}
            title="Verwijderen"
            className="px-2 py-1 text-gray-300 hover:text-red-500"
          >
            <FontAwesomeIcon icon={faTrashCan} size="sm" />
          </button>
        </>
      )}
    </div>
  );
};

// De keuzelijst boven het invoerveld. De focus blijft in het invoerveld: de
// pijltjes verplaatsen de markering, Enter of Tab kiest, de muis markeert waar
// hij boven hangt.
const SkillMenu = ({
  skills,
  highlight,
  onHighlight,
  onPick,
}: {
  skills: SkillDefinition[];
  highlight: number;
  onHighlight: (index: number) => void;
  onPick: (skill: SkillDefinition) => void;
}) => (
  <div className="border-t bg-white px-2 py-1.5 text-xs">
    {skills.length === 0 ? (
      <p className="text-gray-400">Geen skill met die naam.</p>
    ) : (
      <ul role="listbox" aria-label="Skills">
        {skills.map((skill, index) => (
          <li key={skill.name} role="option" aria-selected={index === highlight}>
            <button
              type="button"
              // Niet de focus uit het invoerveld halen: dan werken de pijltjes
              // na een klik ernaast nog.
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => onHighlight(index)}
              onClick={() => onPick(skill)}
              className={`w-full rounded px-2 py-1 text-left ${
                index === highlight ? "bg-yellow/40" : ""
              }`}
            >
              <FontAwesomeIcon icon={faWandMagicSparkles} size="sm" className="mr-1.5" />
              {skill.label}
              <SkillBadges skill={skill} />
            </button>
          </li>
        ))}
      </ul>
    )}
    {/* In een nieuw tabblad: het gesprek blijft staan waar het was. */}
    <a
      href="/skills"
      target="_blank"
      rel="noreferrer"
      className="mt-1 block px-2 text-[11px] text-dark-green underline"
    >
      Skills beheren
    </a>
  </div>
);

export const ChatPanel = ({
  parcels,
  onSelectSession,
  onApplyFilter,
  onResearchProposal,
  onResearchStarted,
}: {
  parcels?: { id: number; name: string }[];
  onSelectSession: (id?: number) => void;
  onApplyFilter?: () => void;
  onResearchProposal?: (proposal: ResearchProposal) => void;
  onResearchStarted?: (researchId: number) => void;
}) => {
  const {
    messages,
    isStreaming,
    error,
    sendMessage,
    stop,
    workspaceId,
    skills,
    loadSkills,
    runSkill,
    startSkillTurn,
  } = useWorkspace();
  const applyFilter = useWorkspaceSearch((s) => s.apply);
  const [input, setInput] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSkill, setActiveSkill] = useState<SkillDefinition>();
  const parcelOptions = useMemo(
    () => (parcels ?? []).map((parcel) => ({ value: parcel.name, label: parcel.name })),
    [parcels]
  );
  const query = slashQuery(input);
  const menuSkills = matchingSkills(skills, query ?? "");
  // Een "/" zonder passende skill is gewoon een vraag die met een schuine
  // streep begint; alleen de knop toont dan nog een (lege) lijst.
  const showMenu = !activeSkill && (menuOpen || (query !== undefined && menuSkills.length > 0));
  const [highlight, setHighlight] = useState(0);
  // De lijst kan korter worden terwijl de markering verder stond.
  const highlighted = Math.min(highlight, Math.max(menuSkills.length - 1, 0));

  useEffect(() => {
    void loadSkills();
    // "Skills beheren" opent in een ander tabblad: wie terugkomt, ziet de
    // skills zoals ze daar zijn opgeslagen.
    const reload = () => {
      if (document.visibilityState === "visible") void loadSkills();
    };
    document.addEventListener("visibilitychange", reload);
    return () => document.removeEventListener("visibilitychange", reload);
  }, [workspaceId, loadSkills]);
  const clickedName = useWorkspaceKavel((state) => state.cube?.name);
  const [dismissed, setDismissed] = useState<string>();
  // Een nieuwe selectie brengt een weggeklikte chip terug.
  useEffect(() => setDismissed(undefined), [clickedName]);
  const chip = chipParcel(activeParcelFor(clickedName, parcels), dismissed);
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const firstFilterMessageIndex = messages.findIndex((message) => message.filters.length > 0);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isStreaming]);

  // Cursor staat klaar bij openen, en weer na een antwoord — zodat doorvragen
  // geen extra klik kost.
  useEffect(() => {
    if (!isStreaming) inputRef.current?.focus();
  }, [isStreaming]);

  const pickSkill = (skill: SkillDefinition) => {
    setMenuOpen(false);
    setInput("");
    // Een beurt zonder velden (het gespreksrapport) heeft geen formulier nodig.
    if (skill.turn && skill.fields.length === 0) {
      if (!isStreaming) void startSkillTurn({ skill: skill.name }, {}, skill.label);
      return;
    }
    setActiveSkill(skill);
  };

  // Pijltjes, Tab en Escape in de lijst; Enter loopt via onSubmit.
  const onMenuKey = (event: KeyboardEvent) => {
    if (!showMenu) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      setHighlight(moveHighlight(highlighted, step, menuSkills.length));
    } else if (event.key === "Tab" && menuSkills.length) {
      event.preventDefault();
      pickSkill(menuSkills[highlighted]);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setMenuOpen(false);
      if (query !== undefined) setInput("");
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    // Enter met de lijst open kiest de gemarkeerde skill, geen vraag aan de chat.
    if (showMenu && menuSkills.length) {
      pickSkill(menuSkills[highlighted]);
      return;
    }
    const trimmed = input.trim();
    if (!trimmed || isStreaming) return;
    setInput("");
    sendMessage(trimmed, chip);
  };

  const startFromMenu = async (skill: SkillDefinition, values: Record<string, any>) => {
    // Een eigen skill is een beurt van het model: het formulier gaat dicht zodra
    // de server de waarden aanneemt, het antwoord streamt in het gesprek.
    if (skill.turn) {
      return startSkillTurn({ skill: skill.name }, values, skill.label, () =>
        setActiveSkill(undefined)
      );
    }
    const outcome = await runSkill(skill.name, values);
    if (!outcome.ok) return outcome.errors;
    setActiveSkill(undefined);
    const filter = outcome.message.filters[0];
    if (filter) {
      applyFilter(filter.id, filter.criteria, filter.label, filter.total ?? undefined);
      onApplyFilter?.();
    }
    const researchId = startedResearch(outcome.message.skillRuns[0]);
    if (researchId) onResearchStarted?.(researchId);
    return undefined;
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <SessionHeader
        onSelect={onSelectSession}
        onReport={() => {
          if (!isStreaming)
            void startSkillTurn({ skill: REPORT_SKILL.name }, {}, REPORT_SKILL.label);
        }}
      />
      <div className="flex-1 min-h-0 space-y-2 overflow-y-auto bg-gray-50 p-3">
        {messages.length === 0 ? (
          <p
            className="text-xs text-gray-400 cursor-text"
            onClick={() => inputRef.current?.focus()}
          >
            Stel een vraag over je percelen, of beschrijf wat voor kavels je zoekt — de assistent
            stelt dan een filter voor. Met / of de toverstaf start je zelf een skill.
          </p>
        ) : (
          messages.map((message, idx) => (
            <Message
              // Een bericht dat nog streamt heeft nog geen id; berichten worden
              // alleen achteraan toegevoegd, dus de index is dan stabiel
              // eslint-disable-next-line @eslint-react/no-array-index-key
              key={message.id ?? idx}
              message={message}
              onApplyFilter={onApplyFilter}
              showFilterTip={idx === firstFilterMessageIndex}
              onResearchProposal={onResearchProposal}
              onResearchStarted={onResearchStarted}
              parcels={parcelOptions}
            />
          ))
        )}
        <div ref={endRef} />
      </div>
      {error && <p className="px-2 pb-1 text-xs text-red-600">{error}</p>}
      {showMenu && (
        <SkillMenu
          skills={menuSkills}
          highlight={highlighted}
          onHighlight={setHighlight}
          onPick={pickSkill}
        />
      )}
      {activeSkill && (
        <div className="max-h-[50%] overflow-y-auto border-t bg-white p-2">
          <p className="mb-1.5 text-xs font-bold">
            <FontAwesomeIcon icon={faWandMagicSparkles} size="sm" className="mr-1" />
            {activeSkill.label}
            <SkillBadges skill={activeSkill} />
          </p>
          <SkillForm
            // Een ander gekozen skill begint met een schoon formulier.
            key={activeSkill.name}
            fields={activeSkill.fields}
            // Alleen een projectperceel: een kavel daarbuiten kent de server niet.
            initial={initialValues(
              activeSkill.fields,
              {},
              chip?.id !== undefined ? chip.name : undefined
            )}
            parcels={parcelOptions}
            disabled={isStreaming}
            onSubmit={(values) => startFromMenu(activeSkill, values)}
            onCancel={() => setActiveSkill(undefined)}
          />
        </div>
      )}
      {chip && (
        <div className="border-t bg-white px-2 pt-1.5 text-[11px]">
          <span className="inline-flex items-center gap-1 rounded-full bg-light-green px-2 py-0.5">
            Over: {chipLabel(chip)}
            <button
              type="button"
              onClick={() => setDismissed(chip.name)}
              className="text-gray-500 hover:text-black"
              title="Niet meesturen met je vraag"
              aria-label="Niet meesturen met je vraag"
            >
              <FontAwesomeIcon icon={faXmark} size="xs" />
            </button>
          </span>
        </div>
      )}
      <form
        onSubmit={onSubmit}
        onKeyDown={onMenuKey}
        className={`flex items-end gap-1.5 bg-white p-2 ${chip ? "" : "border-t"}`}
      >
        <button
          type="button"
          onClick={() => {
            setActiveSkill(undefined);
            setMenuOpen(!menuOpen);
            setHighlight(0);
            // De lijst bedien je vanuit het invoerveld.
            inputRef.current?.focus();
          }}
          aria-expanded={showMenu}
          title="Skill starten"
          aria-label="Skill starten"
          className="rounded-full px-2 py-1.5 text-gray-500 hover:text-black"
        >
          <FontAwesomeIcon icon={faWandMagicSparkles} size="sm" />
        </button>
        <AutoGrowTextarea
          ref={inputRef}
          className="flex-1 rounded-2xl border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs focus:bg-white"
          placeholder="Typ je vraag..."
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setHighlight(0);
          }}
          disabled={isStreaming}
        />
        {isStreaming ? (
          <button
            type="button"
            onClick={stop}
            className="rounded-full bg-dark-green px-3 py-1.5 text-xs font-bold text-white"
            title="Stoppen"
          >
            <FontAwesomeIcon icon={faStop} size="sm" />
          </button>
        ) : (
          <button
            type="submit"
            disabled={!input.trim()}
            className="rounded-full bg-bright-yellow px-3 py-1.5 text-xs font-bold hover:bg-yellow disabled:opacity-40"
          >
            Stuur
          </button>
        )}
      </form>
    </div>
  );
};
